import React, { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { Button, Dialog, Grid, IconButton } from '@material-ui/core'
import { Close } from '@material-ui/icons'

import { useStyles } from './LibraryModal.styles'
import { ILibraryItem } from 'hooks/useLibrary'
import { useIsMobile } from 'shared/hooks'
import { MediaService } from 'shared/services/MediaService'
import axios from 'axios'
import CircularProgress from 'components/ui/DataDisplay/CircularProgressWithLabel'
import { useLanguage } from 'store/locale/hooks/useLanguage'

export interface ILibraryModal {
  isOpen: boolean
  onClose: () => void
  items: ILibraryItem[]
  currentIndex: number
  setCurrentIndex: (index: number) => void
  t: (key: string) => string
}

const LibraryCarousel = lazy(() => import(/* webpackChunkName: "LibraryCarousel" */ '../DataDisplay/LibraryCarousel'))

export const LibraryModal = ({ isOpen, onClose, items, currentIndex, setCurrentIndex, t }: ILibraryModal) => {
  const classes = useStyles()
  const isMobile = useIsMobile()
  const { language } = useLanguage()

  const [progressTotal, setProgressTotal] = useState(1)
  const [progressRatio, setProgressRatio] = useState(1)
  const [titleDownloadInProgress, setTitleDownloadInProgress] = useState('')

  const [cancelTokenSource, setCancelTokenSource] = useState(axios.CancelToken.source())
  const cancelTokenSourceRef = useRef(cancelTokenSource)

  useEffect(() => {
    cancelTokenSourceRef.current = cancelTokenSource
  }, [cancelTokenSource])

  useEffect(() => {
    return () => cancelTokenSourceRef.current.cancel()
  }, [])

  const handleProgress = (loaded, total) => {
    const ratio = loaded / total
    setProgressTotal(Math.ceil(total / 1000000))
    setProgressRatio(ratio)
  }

  const handleDownload = () => {
    if (progressRatio === 1) {
      const item = items[currentIndex]
      const fileExtension = item.url?.split('\\').pop()?.split('/').pop()?.split('.').pop()

      const newCancelTokenSource = axios.CancelToken.source()
      setCancelTokenSource(newCancelTokenSource)
      setTitleDownloadInProgress(item.title[language])

      MediaService.downloadMedia(
        item.url + '?download',
        item.title[language] + '.' + fileExtension,
        handleProgress,
        newCancelTokenSource
      )
    } else {
      setProgressRatio(1)
      cancelTokenSource.cancel()
    }
  }

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth={true} maxWidth="lg" fullScreen={isMobile}>
      <IconButton aria-label="close" onClick={onClose} className={classes.closeButton}>
        <Close />
      </IconButton>
      <Grid container className={classes.modal} direction={isMobile ? 'column-reverse' : 'row'}>
        <Grid item md={4} xs={12}>
          <Grid className={classes.title[language]} style={{ marginBottom: progressRatio < 1 ? '1rem' : '0' }}>
            {items[currentIndex]?.title[language]}
          </Grid>
          <Grid style={{ width: isMobile ? '100%' : '90%' }}>
            {progressRatio < 1 && (
              <Grid container justifyContent="center" alignItems="center">
                <Grid item xs={9} container direction="column">
                  <Grid item>{t('downloadInProgress') + ' ' + titleDownloadInProgress}</Grid>
                  <Grid item style={{ fontSize: '0.6rem' }}>
                    {t('size')} : {progressTotal} {t('MB')}
                  </Grid>
                </Grid>
                <Grid item xs={3} container justifyContent="flex-end" alignItems="center">
                  <CircularProgress value={progressRatio * 100} color="primary" />
                </Grid>
              </Grid>
            )}
            <Button onClick={handleDownload} variant="contained" color="primary" fullWidth={progressRatio < 1}>
              {progressRatio === 1 ? t('download') : t('cancel')}
            </Button>
          </Grid>
        </Grid>
        <Grid item md={8} xs={12} className={classes.carousel}>
          <Suspense fallback={<div></div>}>
            <LibraryCarousel items={items} initialSlide={currentIndex} onChange={setCurrentIndex} />
          </Suspense>
        </Grid>
      </Grid>
    </Dialog>
  )
}
